import {
  Alert,
  Box,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AuthButton, AuthFormContainer, AuthLink, AuthTextField } from "../components/auth";
import { apiClient } from "../utils/api";
import { messageFromDrfError } from "../utils/drfErrorMessage";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the forgot-password API rejects (distinct from simple validation errors). */
  const [passwordApiFailed, setPasswordApiFailed] = useState(false);
  const [passwordApiDetail, setPasswordApiDetail] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    trackEvent(MixpanelEvents.FORGOT_PASSWORD_PAGE_VIEWED, {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setPasswordApiFailed(false);
    setPasswordApiDetail(null);
    trackEvent(MixpanelEvents.FORGOT_PASSWORD_SUBMIT, {});

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(t("auth.emailRequired"));
      setLoading(false);
      return;
    }

    const contactPayload = {
      full_name: t("auth.forgotPasswordContactFullName"),
      email: trimmed,
      message: t("auth.forgotPasswordContactMessage", { email: trimmed }),
    };

    try {
      const [passwordOutcome, contactOutcome] = await Promise.allSettled([
        apiClient.forgotPassword(trimmed),
        apiClient.submitContactForm(contactPayload),
      ]);

      if (contactOutcome.status === "fulfilled") {
        trackEvent(MixpanelEvents.FORGOT_PASSWORD_CONTACT_LOGGED, {});
      } else {
        const creason = contactOutcome.reason;
        trackEvent(MixpanelEvents.FORGOT_PASSWORD_CONTACT_LOG_FAILED, {
          status: (creason as { response?: { status?: number } })?.response?.status,
        });
      }

      if (passwordOutcome.status === "fulfilled") {
        setSuccess(true);
        trackEvent(MixpanelEvents.FORGOT_PASSWORD_EMAIL_SENT, {});
      } else {
        const err = passwordOutcome.reason;
        const detail = messageFromDrfError(err, t("common.error"));
        setPasswordApiFailed(true);
        setPasswordApiDetail(detail);
        trackEvent(MixpanelEvents.FORGOT_PASSWORD_FAILED, {
          status: (err as { response?: { status?: number } })?.response?.status,
        });
      }
    } catch (err: unknown) {
      setPasswordApiFailed(true);
      setPasswordApiDetail(messageFromDrfError(err, t("common.error")));
      trackEvent(MixpanelEvents.FORGOT_PASSWORD_FAILED, {
        status: (err as { response?: { status?: number } })?.response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormContainer>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
      >
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Stack spacing={{ xs: 0.25, md: 0.5 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}
            >
              {t("auth.forgotPassword")}
            </Typography>
          </Stack>

          {success && (
            <Alert severity="success" sx={{ py: { xs: 0.5, md: 1 } }}>
              {t("auth.emailSent")}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ py: { xs: 0.5, md: 1 } }}>
              {error}
            </Alert>
          )}

          {passwordApiFailed && (
            <Alert severity="error" sx={{ py: { xs: 0.5, md: 1 } }}>
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={700}>
                  {t("auth.forgotPasswordRequestFailed")}
                </Typography>
                {passwordApiDetail ? (
                  <Typography variant="body2" sx={{ opacity: 0.92 }}>
                    {passwordApiDetail}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ pt: 0.25 }}>
                  {t("auth.forgotPasswordInstagramRecovery")}{" "}
                  <Link
                    href="https://www.instagram.com/kanplays/"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    fontWeight={700}
                  >
                    @kanplays
                  </Link>
                </Typography>
              </Stack>
            </Alert>
          )}

          {!success ? (
            <>
              <AuthTextField
                fullWidth
                type="email"
                label={t("auth.email")}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <AuthButton
                type="submit"
                variant="primary"
                loading={loading}
              >
                {t("common.submit")}
              </AuthButton>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  pt: { xs: 0.5, md: 0 }
                }}
              >
                <AuthLink to="/login">
                  {t("common.back")}
                </AuthLink>
              </Typography>
            </>
          ) : (
            <AuthButton
              variant="primary"
              onClick={() => navigate("/login")}
            >
              {t("auth.login")}
            </AuthButton>
          )}
        </Stack>
      </Box>
    </AuthFormContainer>
  );
};

export default ForgotPasswordPage;
